import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ZoomInIcon, XIcon } from 'lucide-react';
export function TransformerTheatre() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  return <div className="w-full min-h-screen bg-white" data-id="element-2156">
      {/* Image Modal */}
      {selectedImage && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setSelectedImage(null)} data-id="element-2157">
          <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-white hover:text-emerald-400 transition-colors" data-id="element-2158">
            <XIcon className="w-8 h-8" data-id="element-2159" />
          </button>
          <motion.img initial={{
        scale: 0.9
      }} animate={{
        scale: 1
      }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain" data-id="element-2160" />
        </motion.div>}
      {/* Header */}
      <header className="py-4 px-6 bg-white border-b border-stone-200 sticky top-0 z-40" data-id="element-2161">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-2162">
          <Link to="/work" className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-id="element-2163">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-2164" />
            <span className="font-light" data-id="element-2165">Back to Selected Work</span>
          </Link>
          <h1 className="text-2xl font-light text-stone-900" data-id="element-2166">Maria Elina</h1>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 via-emerald-50/30 to-stone-50" data-id="element-2167">
        <div className="max-w-5xl mx-auto" data-id="element-2168">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-2169">
            <p className="text-emerald-600 font-light tracking-wide uppercase mb-4" data-id="element-2170">
              Interior Architecture, Adaptive Reuse
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-stone-900 mb-6" data-id="element-2171">
              Transformer Theatre
            </h1>
            <p className="text-xl text-stone-600 font-light mb-8" data-id="element-2172">
              A renovation proposition for an abandoned Art Deco building in
              Bournemouth, reimagined as a flexible, multi-configuration
              performance space
            </p>
            <div className="space-y-4 text-stone-700 font-light" data-id="element-2173">
              <p className="text-lg" data-id="element-2174">
                <span className="font-normal text-stone-900" data-id="element-2175">Role:</span>{' '}
                Interior Architecture, Spatial Design
              </p>
              <p className="text-lg" data-id="element-2176">
                <span className="font-normal text-stone-900" data-id="element-2177">Location:</span>{' '}
                Bournemouth, UK
              </p>
              <p className="text-lg" data-id="element-2178">
                <span className="font-normal text-stone-900" data-id="element-2179">Type:</span>{' '}
                Academic Project, Adaptive Reuse
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Project Overview */}
      <section className="py-20 px-6 bg-white" data-id="element-2180">
        <div className="max-w-5xl mx-auto" data-id="element-2181">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2182">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-2183">
              Project Overview
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-2184">
              The Transformer Theatre project reimagines an abandoned Art Deco
              building in Bournemouth as a dynamic, adaptable performance venue.
              The design responds to the evolving needs of contemporary theatre
              and entertainment spaces, creating a facility that can seamlessly
              transitions between multiple configurations to accommodate
              different event types and audience sizes.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-2185">
              Rather than imposing a single-use program on the historic
              structure, the design embraces flexibility as its core principle.
              The "transformer" concept allows the space to adapt from
              traditional proscenium theatre, thrust stage and experimental
              performance configurations to event and gallery venue, maximizing
              the building's utility and cultural relevance.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed" data-id="element-2186">
              The project carefully balances preservation of the building's Art
              Deco character with contemporary entertainment requirements,
              creating a dialogue between historic architecture and modern
              lifestyle.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Design Concept - Radial Diagram */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-2187">
        <div className="max-w-7xl mx-auto" data-id="element-2188">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2189">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-2190">
              Conceptual Framework
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-2191">
              The design concept is visualized through this radial diagram,
              which displays the main stage and strongly references the Art Deco
              heritage of the building while incorporating modern technologies.
              The design intends the usage of many timber-imitating elements
              along with metal and textile, to reference the time period's
              fashion for opulance.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/initial%20sketch1.png')} data-id="element-2192">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-2193">
                <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/initial%20sketch1.png" alt="Conceptual Radial Diagram" className="w-full h-auto" data-id="element-2194" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2195">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2196" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-2197">
                Radial concept sketch illustrating spatial zones, materials and
                aesthetic of the interior
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Interior 3D Rendering */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-2198">
        <div className="max-w-7xl mx-auto" data-id="element-2199">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2200">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-2201">
              Interior 3D Rendering
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-2202">
              The primary configuration of above-ground floors features
              traditional theatre seating arranged in a proscenium format, with
              tiered rows providing optimal sightlines. The basement floor
              configuration demonstrates the space's adaptability, with modular
              staging that can be rearranged to create experimental performance
              layouts.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2203">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(28).png')} data-id="element-2204">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-2205">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(28).png" alt="Traditional Theatre Configuration" className="w-full h-auto" data-id="element-2206" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2207">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2208" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-2209">
                  Isometric view showing traditional proscenium theatre layout
                  with support spaces
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(29).png')} data-id="element-2210">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-2211">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(29).png" alt="Flexible Performance Configuration" className="w-full h-auto padding-20px" data-id="element-2212" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2213">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2214" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-2215">
                  Basement configuration showing adaptable performance space and
                  gallery
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Conceptual Visual Collage */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-2216">
        <div className="max-w-7xl mx-auto" data-id="element-2217">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2218">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-2219">
              Art Deco Meets Contemporary Performance
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-2220">
              This conceptual collage captures the essence of the
              transformation—where historic Art Deco grandeur meets cutting-edge
              performance technology. The layered imagery suggests the
              building's ability to accommodate multiple simultaneous
              experiences, from intimate gatherings to large-scale projections,
              all within the preserved architectural framework.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/Generated%20Image%20October%2026,%202025%20-%2011_33AM.png')} data-id="element-2221">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-2222">
                <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/Generated%20Image%20October%2026,%202025%20-%2011_33AM.png" alt="Conceptual Visual Collage" className="w-full h-auto" data-id="element-2223" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2224">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2225" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-2226">
                Conceptual collage illustrating the fusion of Art Deco
                architecture with contemporary performance technology and
                dynamic spatial experiences
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Extended Time-Based Usage - Evening & Night */}
      <section className="py-20 px-6 bg-white" data-id="element-2227">
        <div className="max-w-7xl mx-auto" data-id="element-2228">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2229">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-2230">
              24-Hour Programming Strategy
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-2231">
              The building's adaptability extends throughout the day,
              transforming from daytime activities into evening performance and
              entertainment. This extended programming maximizes the building's
              utility and creates a vibrant cultural hub that serves the
              community.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2232">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/8%20am%20-%2012%20am.png')} data-id="element-2233">
                <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-2234">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/8%20am%20-%2012%20am.png" alt="Daytime Configurations" className="w-full h-auto" data-id="element-2235" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2236">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2237" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-2238">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-2239">
                    Daytime Configurations
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-2240">
                    Multi-floor usage showing entry spaces, dining areas, staff
                    facilities, workshops, play areas, and technical preparation
                    zones during daytime hours
                  </p>
                </div>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/6%20pm%20-%2010%20pm.png')} data-id="element-2241">
                <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-2242">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/6%20pm%20-%2010%20pm.png" alt="Evening Configurations" className="w-full h-auto" data-id="element-2243" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2244">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2245" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-2246">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-2247">
                    Evening Configurations
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-2248">
                    Prime hours with main auditorium, dining areas, bar
                    facilities, and automated dynamic walls facilitating rapid
                    spatial transformation for evening entertainment
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Architectural Structure */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-2249">
        <div className="max-w-7xl mx-auto" data-id="element-2250">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2251">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-2252">
              Architectural Structure & Layers
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-2253">
              This exploded axonometric reveals the complex spatial organization
              and structural hierarchy of the intervention. The design carefully
              integrates new elements within the existing Art Deco shell,
              creating distinct zones for performance, audience, and support
              functions while maintaining visual and spatial connections
              throughout.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(31).png')} data-id="element-2254">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-2255">
                <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(31).png" alt="Architectural Structure" className="w-full h-auto" data-id="element-2256" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2257">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2258" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-2259">
                3D model section showing architectural layers and structural
                organization
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Functional Zoning & Spatial Organization - Side by Side */}
      <section className="py-20 px-6 bg-white" data-id="element-2260">
        <div className="max-w-7xl mx-auto" data-id="element-2261">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2262">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-2263">
              Functional Zoning & Spatial Organization
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-2264">
              These diagrams illustrate the careful separation and integration
              of different user groups, functions, and programmatic elements.
              The color-coded analysis shows how operational efficiency is
              optimized through strategic spatial organization.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2265">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(32).png')} data-id="element-2266">
                <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-2267">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(32).png" alt="Functional Zoning Above Ground" className="w-full h-auto" data-id="element-2268" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2269">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2270" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-2271">
                  <h3 className="text-lg font-light text-stone-900 mb-2" data-id="element-2272">
                    Functional Zoning Above Ground
                  </h3>
                  <p className="text-stone-600 font-light text-sm" data-id="element-2273">
                    Color-coded diagram showing user group separation and
                    spatial relationships
                  </p>
                </div>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(33).png')} data-id="element-2274">
                <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-2275">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(33).png" alt="Functional Zoning Under Ground" className="w-full h-auto" data-id="element-2276" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2277">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2278" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-2279">
                  <h3 className="text-lg font-light text-stone-900 mb-2" data-id="element-2280">
                    Functional Zoning Under Ground
                  </h3>
                  <p className="text-stone-600 font-light text-sm" data-id="element-2281">
                    Detailed program breakdown showing all functional spaces and
                    their interconnections
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Detailed Intervention Strategy */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-2282">
        <div className="max-w-7xl mx-auto" data-id="element-2283">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2284">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-2285">
              Detailed Intervention Strategy
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-2286">
              The renovation strategy is articulated through detailed sections
              and floor plans that identify specific intervention opportunities.
              Each potential modification is catalogued and evaluated for its
              impact on flexibility, accessibility, and user experience—from
              structural adaptations to service upgrades and spatial
              reconfigurations.
            </p>
            {/* Building Sections with Opportunities */}
            <div className="mb-16" data-id="element-2287">
              <h3 className="text-2xl font-light text-stone-900 mb-6" data-id="element-2288">
                Building Sections: Intervention Opportunities
              </h3>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer mb-8" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/building%20study%20SECTIONS%20(1).png')} data-id="element-2289">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-2290">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/building%20study%20SECTIONS%20(1).png" alt="Building Sections with Intervention Opportunities" className="w-full h-auto" data-id="element-2291" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2292">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2293" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-2294">
                  Sectional analysis identifying potential opportunities for
                  functional improvements
                </p>
              </motion.div>
            </div>
            {/* Floor Plans with Opportunities - Primary Set */}
            <div className="mb-16" data-id="element-2295">
              <h3 className="text-2xl font-light text-stone-900 mb-6" data-id="element-2296">
                Floor Plans: Spatial Intervention Catalog
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2297">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/3.png')} data-id="element-2298">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-2299">
                    <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/3.png" alt="Ground Floor Intervention Plan" className="w-full h-auto" data-id="element-2300" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2301">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2302" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-2303">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-2304">
                      Ground Floor Interventions
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-2305">
                      Detailed catalog of potential modifications including
                      spatial reconfigurations
                    </p>
                  </div>
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.1
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/2.png')} data-id="element-2306">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-2307">
                    <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/2.png" alt="Upper Floor Intervention Plan" className="w-full h-auto" data-id="element-2308" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2309">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2310" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-2311">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-2312">
                      Upper Floor Interventions
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-2313">
                      Strategic modifications for backstage areas, technical
                      spaces, and support facilities to enhance operational
                      efficiency
                    </p>
                  </div>
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.2
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/1.png')} data-id="element-2314">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-2315">
                    <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/1.png" alt="Backstage Level Intervention Plan" className="w-full h-auto" data-id="element-2316" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2317">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2318" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-2319">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-2320">
                      Backstage Level Interventions
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-2321">
                      Performer facilities, green rooms, and technical support
                      spaces
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
            {/* Time-Based Usage Diagrams */}
          </motion.div>
        </div>
      </section>
      {/* Site Analysis & Environmental Context */}
      <section className="py-20 px-6 bg-white" data-id="element-2322">
        <div className="max-w-7xl mx-auto" data-id="element-2323">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2324">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-2325">
              Site Analysis & Environmental Context
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-2326">
              The site's environmental conditions were considered in the design
              strategy. This solar analysis maps sun and wind angles throughout
              the year, informing decisions about the relationship between
              interior spaces and seasonal variations.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/3d%20weather.png')} data-id="element-2327">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-2328">
                <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/3d%20weather.png" alt="Site Solar Analysis" className="w-full h-auto" data-id="element-2329" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-2330">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2331" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-2332">
                Solar and wind map analysis with massing showing variables
                across different time
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Design Approach */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-2333">
        <div className="max-w-5xl mx-auto" data-id="element-2334">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2335">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-2336">
              Design Philosophy & Approach
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed" data-id="element-2337">
              <p data-id="element-2338">
                The Transformer Theatre project emerged from a fundamental
                question: how can historic architecture accommodate the evolving
                nature of contemporary performance? Rather than treating the Art
                Deco building as a fixed container, the design embraces
                flexibility as both a practical necessity and a conceptual
                driver.
              </p>
              <p data-id="element-2339">
                The "transformer" concept operates on multiple levels. At the
                most literal, modular seating systems and adaptable stage
                configurations allow the space to physically transform between
                different theatrical formats. But more profoundly, the design
                transforms the relationship between building and program,
                creating a venue that can evolve with changing cultural needs
                rather than becoming obsolete.
              </p>
              <p data-id="element-2340">
                The preservation strategy respects the building's Art Deco
                heritage while introducing contemporary interventions that are
                clearly legible as new. Ultimately, the project demonstrates how
                adaptive reuse can breathe new life into abandoned buildings,
                transforming them into vibrant cultural assets that serve their
                communities in multiple, evolving ways.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}